import { Directive, effect, ElementRef, inject, Injector, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, of, startWith, Subject, switchMap } from 'rxjs';
import { OverlayRef, OverlaysService, POSITION_TARGET, PositionTarget } from '../overlays';
import { TooltipComponent } from './tooltip.component';


let copyLastId = 0;


@Directive({
    selector: 'a[copyLink]',
    host: {
        'tabindex': '',
        '(click)': 'onClick($event)',
    }
})
export class CopyLinkDirective implements OnDestroy, PositionTarget {

    copyLink = input.required<string | null>();

    private tooltipId = `tooltip${++copyLastId}`;

    private copyFeedback = new Subject<void>();

    private show = signal(false);

    private tooltip: OverlayRef | null = null;

    private elementRef = inject(ElementRef);

    private overlaysService = inject(OverlaysService);

    private injector = inject(Injector);

    constructor() {
        effect(() => {
            if (this.show()) {
                this.showTooltip();
            } else {
                this.hideTooltip();
            }
        });

        this.copyFeedback.pipe(
            switchMap(_ => of(false).pipe(delay(3_000), startWith(true))),
            takeUntilDestroyed()
        ).subscribe(fb => {
            this.show.set(fb);
        });
    }

    getBoundingClientRect() {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }

    onClick(event: Event) {
        event.stopImmediatePropagation();
        navigator.clipboard.writeText(this.copyLink() || '');
        this.copyFeedback.next();
    }

    private showTooltip() {
        if (!this.elementRef.nativeElement.parentElement) {
            return;
        }
        if (this.tooltip) {
            return;
        }
        const injector = Injector.create({
            providers: [{ provide: POSITION_TARGET, useValue: this }],
            parent: this.injector,
        });
        this.tooltip = this.overlaysService.createOverlay(TooltipComponent, {
            inputs: {
                content: 'Copied',
                placement: 'right',
                tooltipId: this.tooltipId,
            },
            parentInjector: injector,
        });
        this.tooltip.componentRef.onDestroy(() => {
            this.tooltip = null;
        });
    }

    private hideTooltip() {
        if (this.tooltip) {
            this.tooltip.dismiss('hide');
        }
    }

    ngOnDestroy() {
        this.hideTooltip();
    }
}
